import { useQuery } from '@tanstack/react-query';
import type { CourseMappingResource, ListCourseMappingsResponse } from '@ab/ams-school-api/types/private/v1';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { IsNullOrUndefined } from '~/utils/isNullOrUndefined';

export type CourseMappingsSearchQuery = {
  'filter[ids]'?: string[];
  'filter[archived]'?: boolean;
  'filter[country]'?: string[];
  sort?: string;
  'page[size]': number;
  'page[number]': number;
};

type UseCourseMappingsOptions = {
  onSuccess?: (courseMappings: CourseMappingResource[], total: number) => void;
  keepPreviousData?: boolean;
  enabled?: boolean;
};

export function useCourseMappings(query: CourseMappingsSearchQuery, options?: UseCourseMappingsOptions) {
  const request = useRequest();
  const queryKey = ['courseMappings', query];

  const { isLoading, isFetching, data, error, isPreviousData } = useQuery(
    queryKey,
    async () => {
      const queryParams = convertToUrlSearchParams(query);
      const response = await request<ListCourseMappingsResponse>(
        `${config.apiHost}/private/v1/course_mappings?${queryParams}`,
        {
          method: 'GET',
          isExpectedResponse,
        }
      );
      return response;
    },
    {
      keepPreviousData: options?.keepPreviousData ?? true,
      enabled: options?.enabled ?? true,
      onSuccess: (response) => {
        options?.onSuccess?.(response.data, response.meta.pagination.total);
      },
    }
  );

  return {
    courseMappings: data?.data ?? [],
    courseMappingsPagination: data?.meta.pagination,
    totalCourseMappings: data?.meta.pagination?.total ?? 0,
    isPreviousCourseMappingsData: isPreviousData,
    courseMappingsError: error,
    isFetchingCourseMappings: isFetching,
    isLoadingCourseMappings: isLoading,
  };
}

const convertToUrlSearchParams = (params: CourseMappingsSearchQuery, baseKey?: string) => {
  const queryParams = {} as Record<string, string>;
  (Object.entries(params) as Array<[string, string | number | boolean | string[]]>).forEach(([k, v]) => {
    const key = (baseKey && `${baseKey}[${k}]`) || k;
    if (IsNullOrUndefined(v)) {
      return;
    }
    if (typeof v === 'string' && v.length > 0) {
      queryParams[key] = v;
    } else if (typeof v === 'number') {
      queryParams[key] = `${v}`;
    } else if (typeof v === 'boolean' && v) {
      queryParams[key] = 't';
    } else if (Array.isArray(v) && v.length > 0) {
      queryParams[key] = v.join(',');
    }
  });

  // Requests field of study and program level information
  queryParams['include'] = 'equivalent_course,education_level';

  return new URLSearchParams(queryParams);
};

function isExpectedResponse(res: unknown): res is ListCourseMappingsResponse {
  if (!res || typeof res !== 'object') {
    return false;
  }
  const candidate = res as ListCourseMappingsResponse;
  return Boolean(candidate.data && Array.isArray(candidate.data));
}
